import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { cn } from "../lib/utils.js";

/* The bordered name tag drawn for a remote peer (presence-tag): PresenceCursors
   and WidgetLayer's RemoteWidgetActivityOverlay both draw it, and each keeps
   its own internal shape — the cursor tag truncates on itself, the activity
   tag truncates a name span beside an optional "+N" — so neither picture
   moves. `overflowCount` being passed at all (even 0) is what selects the
   flex/name-span shape; omitting it keeps the single-span shape. */

export interface PresenceTagProps extends Omit<
  ComponentPropsWithoutRef<"span">,
  "title"
> {
  /** The peer's name, or any other tag content. */
  name: ReactNode;
  /** The awareness colour (`PresenceUser.color`), painted as the border. */
  color: string;
  /** A muted `+N` after the name. `0` or omitted renders nothing. */
  overflowCount?: number;
}

const PRESENCE_TAG_LOOK =
  "rounded-md border border-pb-border bg-pb-card px-2 py-0.5 text-dense-1 font-semibold text-pb-text shadow-sm";

/**
 * The bordered name tag PresenceCursors and WidgetLayer's
 * RemoteWidgetActivityOverlay both draw for a remote peer. Data-blind: the
 * awareness colour is passed in, never looked up. `className` is for
 * placement only (margin, max-width) — the tag owns its own look.
 */
export const PresenceTag = forwardRef<HTMLSpanElement, PresenceTagProps>(
  function PresenceTag(
    { name, color, overflowCount, className, style, ...rest },
    ref,
  ) {
    const showOverflow = overflowCount !== undefined;
    return (
      <span
        ref={ref}
        className={cn(
          PRESENCE_TAG_LOOK,
          showOverflow ? "flex items-center gap-1" : "block",
          className,
        )}
        style={{ ...style, borderColor: color }}
        {...rest}
      >
        {showOverflow ? <span className="truncate">{name}</span> : name}
        {overflowCount ? (
          <span className="shrink-0 text-pb-text-muted">+{overflowCount}</span>
        ) : null}
      </span>
    );
  },
);
